/**
 * Secure storage utility for sensitive data
 * Uses Web Crypto API for encryption with a device-specific key
 */

const STORAGE_PREFIX = 'sc_secure_'
const REMEMBER_ME_KEY = 'remember_me_preference'
const LOGIN_TIMESTAMP_KEY = 'login_timestamp'
const LAST_ORG_KEY = 'last_auth0_org'

// Session duration constants
const REMEMBER_ME_DURATION = 7 * 24 * 60 * 60 * 1000 // 7 days in milliseconds
const SESSION_ONLY_DURATION = 24 * 60 * 60 * 1000 // 24 hours in milliseconds (for non-remembered logins)

/**
 * Gets or creates the AES-GCM encryption key for secure storage.
 * The key is persisted in localStorage so it survives browser restarts, allowing
 * the "remember me" preference to be decrypted on the next visit.
 */
async function getOrCreateEncryptionKey(): Promise<CryptoKey> {
  // Key is stored in localStorage (not sessionStorage) so that it persists across
  // browser restarts. The data it protects (remember-me preference + login
  // timestamp) is not sensitive, so persisting the key in localStorage is safe.
  const keyData = localStorage.getItem(`${STORAGE_PREFIX}crypto_key`)

  if (keyData) {
    try {
      const keyBuffer = Uint8Array.from(atob(keyData), c => c.charCodeAt(0))
      return await crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      )
    } catch {
      // If import fails, generate a new key
    }
  }

  // Generate a new key
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ])

  // Persist the key in localStorage so it survives browser close/reopen
  const exportedKey = await crypto.subtle.exportKey('raw', key)
  const keyString = btoa(String.fromCharCode(...new Uint8Array(exportedKey)))
  localStorage.setItem(`${STORAGE_PREFIX}crypto_key`, keyString)

  return key
}

/**
 * Encrypts data using AES-GCM
 */
async function encryptData(data: string): Promise<string> {
  const key = await getOrCreateEncryptionKey()
  const iv = crypto.getRandomValues(new Uint8Array(12)) // 96-bit IV for AES-GCM
  const encoder = new TextEncoder()
  const encodedData = encoder.encode(data)

  const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encodedData)

  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encryptedData.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(encryptedData), iv.length)

  return btoa(String.fromCharCode(...combined))
}

/**
 * Decrypts data using AES-GCM
 */
async function decryptData(encryptedString: string): Promise<string | null> {
  try {
    const key = await getOrCreateEncryptionKey()
    const combined = Uint8Array.from(atob(encryptedString), c => c.charCodeAt(0))

    // Extract IV and encrypted data
    const iv = combined.slice(0, 12)
    const encryptedData = combined.slice(12)

    const decryptedData = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encryptedData)

    const decoder = new TextDecoder()
    return decoder.decode(decryptedData)
  } catch {
    return null
  }
}

/**
 * Securely stores a value in localStorage with encryption
 */
export async function secureSetItem(key: string, value: string): Promise<void> {
  const encrypted = await encryptData(value)
  localStorage.setItem(`${STORAGE_PREFIX}${key}`, encrypted)
}

/**
 * Retrieves and decrypts a value from localStorage
 */
export async function secureGetItem(key: string): Promise<string | null> {
  const encrypted = localStorage.getItem(`${STORAGE_PREFIX}${key}`)
  if (!encrypted) {
    return null
  }
  return await decryptData(encrypted)
}

/**
 * Removes an item from secure storage
 */
export function secureRemoveItem(key: string): void {
  localStorage.removeItem(`${STORAGE_PREFIX}${key}`)
}

/**
 * Clears all secure storage items including the persisted encryption key.
 */
export function secureClearAll(): void {
  const keys = Object.keys(localStorage)
  keys.forEach(key => {
    if (key.startsWith(STORAGE_PREFIX)) {
      localStorage.removeItem(key)
    }
  })
}

/**
 * Sets the remember me preference
 */
export async function setRememberMePreference(remember: boolean): Promise<void> {
  await secureSetItem(REMEMBER_ME_KEY, remember.toString())
  // Store login timestamp to track session expiration
  await secureSetItem(LOGIN_TIMESTAMP_KEY, Date.now().toString())
}

/**
 * Gets the remember me preference
 */
export async function getRememberMePreference(): Promise<boolean> {
  const value = await secureGetItem(REMEMBER_ME_KEY)
  return value === 'true'
}

/**
 * Checks if the current session should be expired based on remember me preference
 * Returns true if session should be considered expired
 */
export async function shouldExpireSession(): Promise<boolean> {
  const rememberMe = await getRememberMePreference()
  const loginTimestampStr = await secureGetItem(LOGIN_TIMESTAMP_KEY)

  // If no login timestamp, can't determine - assume not expired
  if (!loginTimestampStr) {
    return false
  }

  const loginTimestamp = parseInt(loginTimestampStr, 10)
  if (isNaN(loginTimestamp)) {
    return false
  }

  const now = Date.now()
  const sessionAge = now - loginTimestamp

  // If remember me is enabled, allow 7 days
  // Otherwise, only allow 24 hours
  const maxDuration = rememberMe ? REMEMBER_ME_DURATION : SESSION_ONLY_DURATION

  return sessionAge > maxDuration
}

/**
 * Clears the remember me preference
 */
export function clearRememberMePreference(): void {
  secureRemoveItem(REMEMBER_ME_KEY)
  secureRemoveItem(LOGIN_TIMESTAMP_KEY)
}

/**
 * Removes the legacy last-login Auth0 organization id (#860) that older builds saved.
 * Nothing reads it since #2909; callers purge it best-effort.
 */
export function clearLastOrgId(): void {
  secureRemoveItem(LAST_ORG_KEY)
}
